import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { auth } from '../config/firebase';
import { applyActionCode, reload, checkActionCode, isSignInWithEmailLink, signInWithEmailLink, sendSignInLinkToEmail } from 'firebase/auth';
import { getFirebaseErrorMessage } from '../utils/firebaseErrors';
import './EmailVerification.css';

const EmailVerification = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState('verifying'); // 'verifying', 'success', 'error', 'needs-email'
  const [error, setError] = useState(null);
  const [emailInput, setEmailInput] = useState('');

  // Helper function to complete email link sign-in
  const completeEmailLinkSignIn = async (email, fullUrl) => {
    try {
      console.log('🔍 [EMAIL_LINK_SIGNIN] Starting sign-in process');
      console.log('🔍 [EMAIL_LINK_SIGNIN] Email:', email);
      console.log('🔍 [EMAIL_LINK_SIGNIN] Full URL:', fullUrl);
      console.log('🔍 [EMAIL_LINK_SIGNIN] Current auth state before sign-in:', auth.currentUser ? `Signed in as ${auth.currentUser.email}` : 'Not signed in');
      
      // Sign in with email link - this automatically verifies email AND signs them in
      console.log('🔍 [EMAIL_LINK_SIGNIN] Calling signInWithEmailLink...');
      const userCredential = await signInWithEmailLink(auth, email, fullUrl);
      console.log('✅ [EMAIL_LINK_SIGNIN] signInWithEmailLink completed successfully');
      console.log('🔍 [EMAIL_LINK_SIGNIN] UserCredential:', {
        user: userCredential.user ? {
          uid: userCredential.user.uid,
          email: userCredential.user.email,
          emailVerified: userCredential.user.emailVerified
        } : null
      });
      
      // Check auth state immediately after sign-in
      const immediateUser = auth.currentUser;
      console.log('🔍 [EMAIL_LINK_SIGNIN] auth.currentUser immediately after signInWithEmailLink:', immediateUser ? {
        uid: immediateUser.uid,
        email: immediateUser.email,
        emailVerified: immediateUser.emailVerified
      } : 'null');
      
      // Reload user to ensure emailVerified status is updated
      let currentUser = userCredential.user;
      if (currentUser) {
        console.log('🔍 [EMAIL_LINK_SIGNIN] Reloading user to update emailVerified status...');
        await reload(currentUser);
        currentUser = auth.currentUser; // Get fresh user after reload
        console.log('✅ [EMAIL_LINK_SIGNIN] User reloaded. Email verified:', currentUser?.emailVerified);
        console.log('🔍 [EMAIL_LINK_SIGNIN] Full user object after reload:', currentUser ? {
          uid: currentUser.uid,
          email: currentUser.email,
          emailVerified: currentUser.emailVerified,
          providerData: currentUser.providerData.map(p => p.providerId)
        } : 'null');
        
        // Verify email is actually verified
        if (!currentUser?.emailVerified) {
          console.warn('⚠️ [EMAIL_LINK_SIGNIN] Email still not verified after reload, waiting...');
          // Wait a bit more and check again
          await new Promise(resolve => setTimeout(resolve, 1000));
          await reload(currentUser);
          currentUser = auth.currentUser;
          console.log('✅ [EMAIL_LINK_SIGNIN] Re-checked. Email verified:', currentUser?.emailVerified);
        }
      } else {
        console.error('❌ [EMAIL_LINK_SIGNIN] userCredential.user is null!');
      }
      
      // Clear the stored email
      localStorage.removeItem('pendingVerificationEmail');
      
      // Wait for auth state to stabilize and AuthContext to sync
      // Give AuthContext time to process the sign-in and sync to database
      console.log('🔍 [EMAIL_LINK_SIGNIN] Setting status to success and waiting for AuthContext...');
      setStatus('success');
      
      // Check auth state multiple times to see if it persists
      const checkAuthState = () => {
        const checkUser = auth.currentUser;
        console.log('🔍 [EMAIL_LINK_SIGNIN] Auth state check:', checkUser ? {
          uid: checkUser.uid,
          email: checkUser.email,
          emailVerified: checkUser.emailVerified
        } : 'null');
        return checkUser;
      };
      
      // Check immediately
      checkAuthState();
      
      // Check after 1 second
      setTimeout(() => {
        console.log('🔍 [EMAIL_LINK_SIGNIN] Auth state after 1 second:');
        checkAuthState();
      }, 1000);
      
      // Check after 2 seconds
      setTimeout(() => {
        console.log('🔍 [EMAIL_LINK_SIGNIN] Auth state after 2 seconds:');
        checkAuthState();
      }, 2000);
      
      // Wait longer for AuthContext to sync, then navigate
      // The auth state change listener in AuthContext will handle the sync
      setTimeout(() => {
        console.log('🔍 [EMAIL_LINK_SIGNIN] Final auth state check before navigation:');
        const finalUser = checkAuthState();
        console.log('✅ [EMAIL_LINK_SIGNIN] Navigating to home after sign-in');
        console.log('👤 [EMAIL_LINK_SIGNIN] Final user state:', finalUser ? {
          email: finalUser.email,
          verified: finalUser.emailVerified,
          uid: finalUser.uid
        } : 'null');
        navigate('/');
      }, 3000);
    } catch (err) {
      console.error('❌ [EMAIL_LINK_SIGNIN] Email link sign-in error:', err);
      console.error('❌ [EMAIL_LINK_SIGNIN] Error details:', {
        code: err.code,
        message: err.message,
        stack: err.stack
      });
      setStatus('error');
      setError(getFirebaseErrorMessage(err));
    }
  };

  // Handle email submission from form
  const handleEmailSubmit = async (e) => {
    e.preventDefault();
    if (!emailInput || !emailInput.includes('@')) {
      setError('Please enter a valid email address.');
      return;
    }
    
    setStatus('verifying');
    setError(null);
    const fullUrl = window.location.href;
    await completeEmailLinkSignIn(emailInput.trim(), fullUrl);
  };

  useEffect(() => {
    const verifyEmail = async () => {
      const mode = searchParams.get('mode');
      const oobCode = searchParams.get('oobCode');
      const fullUrl = window.location.href;

      // Check if this is an email link (sign-in link) or verification link
      const isEmailLink = isSignInWithEmailLink(auth, fullUrl);
      
      if (isEmailLink) {
        // This is a sign-in link - it will both verify email AND sign the user in
        console.log('🔗 Detected email sign-in link');
        
        try {
          let email = null;
          
          // Priority 1: Try to extract from action code (most reliable - encoded in the link itself)
          const oobCode = searchParams.get('oobCode') || searchParams.get('oobcode');
          if (oobCode) {
            try {
              const actionCodeInfo = await checkActionCode(auth, oobCode);
              email = actionCodeInfo.data?.email;
              if (email) {
                console.log('✅ Extracted email from action code');
              }
            } catch (checkError) {
              console.warn('⚠️ Could not extract email from action code:', checkError.code);
            }
          }
          
          // Priority 2: Fall back to localStorage (less reliable - might be from different device/browser)
          if (!email) {
            email = localStorage.getItem('pendingVerificationEmail');
          }
          
          // If still no email, show form to user
          if (!email) {
            setStatus('needs-email');
            return;
          }
          
          // We have email, proceed with sign-in
          await completeEmailLinkSignIn(email, fullUrl);
        } catch (err) {
          console.error('❌ Email link sign-in error:', err);
          setStatus('error');
          setError(getFirebaseErrorMessage(err));
        }
        return;
      }

      // Fallback: Handle regular email verification (for backwards compatibility)
      if (!oobCode) {
        setStatus('error');
        setError('Invalid verification link. Please check your email and try again.');
        return;
      }

      // Accept both 'verifyEmail' and 'action' modes
      if (mode !== 'verifyEmail' && mode !== 'action') {
        setStatus('error');
        setError('Invalid verification link. Please check your email and try again.');
        return;
      }

      try {
        // First, check the action code to get the email before applying it
        let userEmail = null;
        try {
          const actionCodeInfo = await checkActionCode(auth, oobCode);
          userEmail = actionCodeInfo.data.email;
          console.log('📧 Email from verification link:', userEmail);
        } catch (checkError) {
          console.warn('⚠️ Could not check action code (this is okay):', checkError);
        }
        
        // Apply the verification code
        await applyActionCode(auth, oobCode);
        console.log('✅ [EMAIL_VERIFY] Email verification code applied successfully');
        
        // Check if user is currently signed in to Firebase
        let currentUser = auth.currentUser;
        console.log('🔍 [EMAIL_VERIFY] Current user after verification:', currentUser ? {
          uid: currentUser.uid,
          email: currentUser.email,
          emailVerified: currentUser.emailVerified
        } : 'not signed in');
        
        // If user is signed in, reload to get updated emailVerified status and let AuthContext sync
        if (currentUser) {
          console.log('👤 [EMAIL_VERIFY] User is signed in, reloading to get updated emailVerified status...');
          await reload(currentUser);
          currentUser = auth.currentUser;
          console.log('✅ [EMAIL_VERIFY] User reloaded. Email verified:', currentUser?.emailVerified);
          
          // Wait a moment for AuthContext to detect the change and sync
          await new Promise(resolve => setTimeout(resolve, 1500));
          
          // Check if user is still signed in (AuthContext might have synced them)
          const finalUser = auth.currentUser;
          console.log('🔍 [EMAIL_VERIFY] Final check - user still signed in:', finalUser ? 'yes' : 'no');
          
          setStatus('success');
          setTimeout(() => {
            console.log('✅ [EMAIL_VERIFY] Navigating to home');
            navigate('/');
          }, 1000);
        } else {
          // User is not signed in - we can't automatically sign them in without their password
          // Redirect to login with email pre-filled
          console.log('🔍 [EMAIL_VERIFY] User not signed in after verification - redirecting to login');
          
          const emailToUse = userEmail || localStorage.getItem('pendingVerificationEmail');
          
          // Store verified email for login page
          if (emailToUse) {
            sessionStorage.setItem('verifiedEmail', emailToUse);
            localStorage.removeItem('pendingVerificationEmail');
          }
          
          // Show success and redirect to login
          // The login page will have the email pre-filled and show a success message
          setStatus('success');
          setTimeout(() => {
            console.log('🔍 [EMAIL_VERIFY] Redirecting to login with verified email:', emailToUse);
            navigate('/login', { 
              state: { 
                verified: true,
                email: emailToUse,
                message: 'Your email has been verified! Please sign in to continue.'
              } 
            });
          }, 2000);
        }
      } catch (err) {
        console.error('Email verification error:', err);
        setStatus('error');
        setError(getFirebaseErrorMessage(err));
      }
    };

    verifyEmail();
  }, [searchParams, navigate]);

  if (status === 'verifying') {
    return (
      <div className="email-verification">
        <div className="verification-container">
          <div className="loading-spinner"></div>
          <h2>Verifying your email...</h2>
          <p>Please wait while we verify your email address.</p>
        </div>
      </div>
    );
  }

  if (status === 'needs-email') {
    return (
      <div className="email-verification">
        <div className="verification-container">
          <h2>Complete Sign-In</h2>
          <p>Please enter the email address associated with this sign-in link:</p>
          <form onSubmit={handleEmailSubmit} style={{ marginTop: '1rem' }}>
            <input
              type="email"
              value={emailInput}
              onChange={(e) => setEmailInput(e.target.value)}
              placeholder="Enter your email address"
              required
              autoFocus
              style={{
                width: '100%',
                padding: '0.75rem',
                fontSize: '1rem',
                border: '1px solid #ddd',
                borderRadius: '4px',
                marginBottom: '1rem'
              }}
            />
            {error && (
              <div style={{ 
                color: '#f44336', 
                marginBottom: '1rem',
                fontSize: '0.9rem'
              }}>
                {error}
              </div>
            )}
            <button 
              type="submit"
              className="primary-button"
              style={{ width: '100%' }}
            >
              Continue Sign-In
            </button>
          </form>
          <button 
            onClick={() => navigate('/login')}
            style={{ 
              marginTop: '1rem',
              background: 'transparent',
              border: 'none',
              color: '#666',
              cursor: 'pointer',
              textDecoration: 'underline'
            }}
          >
            Back to Login
          </button>
        </div>
      </div>
    );
  }

  if (status === 'success') {
    return (
      <div className="email-verification">
        <div className="verification-container success">
          <div className="success-icon">✓</div>
          <h2>Email Verified!</h2>
          <p>Your email address has been successfully verified.</p>
          <p>{auth.currentUser ? 'Signing you in...' : 'Redirecting you to sign in...'}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="email-verification">
      <div className="verification-container error">
        <div className="error-icon">✗</div>
        <h2>Verification Failed</h2>
        <p>{error}</p>
        <button 
          onClick={() => navigate('/')}
          className="primary-button"
        >
          Go to Home
        </button>
      </div>
    </div>
  );
};

export default EmailVerification;

